<template>
	<App title="Zone Management">
		<div class="w-100 position-relative">
			<Block class="border-top border-secondary border-2 col-md-10 mx-auto">
				<template #header>
					<h3 class="block-title">Zone Management</h3>
				</template>

				<!-- Zone Management Form -->
				<div class="row">
					<div class="col-12">
						<div class="card border-0">
							<div class="card-header bg-light border-0">
								<h5 class="card-title mb-0">
									<i class="bi bi-map-fill text-secondary me-2"></i>
									Create New Zone
								</h5>
							</div>
							<div class="card-body p-4">
								<Form ref="zoneForm" @submit="onZoneSubmit">
									<div class="row g-3">
										<!-- Country Selection -->
										<div class="col-12 col-md-6">
											<label for="zoneCountry" class="form-label text-dark fw-normal"> <i class="bi bi-flag me-1"></i>Country * </label>
											<SelectField id="zoneCountry" v-model="zoneData.country" name="country" rules="required" label="Country" :options="country_options"> </SelectField>
										</div>

										<!-- Region Selection -->
										<div class="col-12 col-md-6">
											<label for="zoneRegion" class="form-label text-dark fw-normal"> <i class="bi bi-geo-alt me-1"></i>Region * </label>
											<SelectField id="zoneRegion" v-model="zoneData.region" name="region" rules="required" label="Region" :options="region_options" :disabled="!zoneData.country"> </SelectField>
										</div>

										<!-- State Selection -->
										<div class="col-12 col-md-6">
											<label for="zoneState" class="form-label text-dark fw-normal"> <i class="bi bi-building me-1"></i>State * </label>
											<SelectField id="zoneState" v-model="zoneData.state" name="state" rules="required" label="State" :options="state_options" :disabled="!zoneData.region"> </SelectField>
										</div>

										<!-- Zone Name -->
										<div class="col-12 col-md-6">
											<label for="zoneName" class="form-label text-dark fw-normal"> <i class="bi bi-map me-1"></i>Zone Name * </label>
											<InputField id="zoneName" v-model="zoneData.zone" name="zone" rules="required|min:3|max:50" label="Zone Name" placeholder="Enter zone name..." />
										</div>

										<div class="col-12">
											<label for="zoneManager" class="form-label text-dark fw-normal"> <i class="bi bi-person-badge me-1"></i>Manager (Optional) </label>
											<SelectField id="zoneManager" v-model="zoneData.manager" name="manager" label="Manager" :options="manager_options"> </SelectField>
										</div>

										<!--
										<div class="col-12">
											<label for="zoneDescription" class="form-label text-dark fw-normal">
												<i class="bi bi-card-text me-1"></i>Description (Optional)
											</label>
											<TextareaField
												id="zoneDescription"
												v-model="zoneData.description"
												name="description"
												label="Description"
												placeholder="Enter zone description..."
												rows="3"
											/>
										</div>
										-->
										<!-- Form Buttons -->
										<div class="col-12">
											<div class="d-grid gap-2 d-md-flex justify-content-md-end pt-3">
												<button type="button" class="btn bg-danger text-white me-2 px-4" :disabled="zoneLoading || zoneDone" @click="clearZoneForm"><i class="bi bi-x-circle me-1"></i>Clear</button>
												<Button id="zoneAction" type="submit" :label="zoneLabels" :loading="zoneLoading" :done="zoneDone" class="btn bg-secondary text-light px-4" :disabled="checkZoneInput" />
											</div>
										</div>
									</div>
								</Form>
							</div>
						</div>
					</div>
				</div>
			</Block>

			<!-- Notification Area -->
			<div class="mt-9 position-absolute top-0 end-0">
				<NotivueKeyboard v-slot="{ containersTabIndex }">
					<Notivue v-slot="item" :containersTabIndex="containersTabIndex">
						<AppNotification :item="item" />
					</Notivue>
				</NotivueKeyboard>
			</div>
		</div>
	</App>
</template>

<script lang="ts">
import { useConfirm } from '@/libs/useConfirm.ts'
import Alert from '@/vue/components/Alert.vue'
import AppNotification from '@/vue/components/AppNotification.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import TextareaField from '@/vue/components/TextareaField.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import $ from 'jquery'
import { materialTheme, Notivue, NotivueKeyboard, push } from 'notivue'
import { Options as Select2Options } from 'select2'
import Swal from 'sweetalert2'
import { Component, Ref, toNative, Vue, Watch } from 'vue-facing-decorator'

@Component({
	components: {
		App,
		Form,
		Alert,
		InputField,
		Button,
		Block,
		SelectField,
		TextareaField,
		Notivue,
		AppNotification,
		NotivueKeyboard,
	},
})
class ZoneManagement extends Vue {
	confirm = useConfirm().confirm
	materialTheme = materialTheme

	// Zone Form Data
	zoneLoading = false
	zoneDone = false
	zoneData = {
		country: '',
		region: '',
		state: '',
		zone: '',
		manager: '',
		description: '',
	}

	// Mock data - replace with actual API calls

	@Ref('zoneForm')
	readonly zoneForm!: InstanceType<typeof Form>

	// Computed Properties
	get country_options(): Select2Options {
		return {
			placeholder: 'Select Country',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('country.ajax.select.country'),
			},

			//@ts-ignore
			templateResult: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
		}
	}

	get region_options(): Select2Options {
		return {
			placeholder: 'Select Region',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('region.ajax.select.region'),
			},

			//@ts-ignore
			templateResult: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
		}
	}

	get state_options(): Select2Options {
		return {
			placeholder: 'Select State',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('state.ajax.select.state'),
			},

			//@ts-ignore
			templateResult: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
		}
	}
	get manager_options(): Select2Options {
		return {
			placeholder: 'Select Manager',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('admin.ajax.select.admin'),
			},

			//@ts-ignore
			templateResult: ({ id, fullname, email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, fullname, email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
		}
	}

	get zoneLabels(): ButtonLabel {
		return {
			normal: 'Create Zone',
			loading: 'Saving...',
			done: 'Saved',
		}
	}

	get checkZoneInput(): boolean {
		return [this.zoneData.country, this.zoneData.region, this.zoneData.state, this.zoneData.zone].some((field) => field.trim() === '')
	}

	// Methods`~
	clearZoneForm() {
		this.zoneData = {
			country: '',
			region: '',
			state: '',
			name: '',
			manager: '',
			description: '',
		}
		this.zoneForm.reset()
	}

	@Watch('zoneData.country')
	onZoneCountryChange() {
		this.zoneData.region = ''
		this.zoneData.state = ''
	}

	@Watch('zoneData.region')
	onZoneRegionChange() {
		this.zoneData.state = ''
	}

	async onZoneSubmit(payload) {
		this.zoneLoading = true
		this.$http
			.post(this.route('zone.page.create'), payload)
			.then((response) => {
				Swal.fire({
					title: 'Success!',
					text: 'Zone created successfully!',
					icon: 'success',
					confirmButtonText: 'OK',
				}).then(() => {
					this.clearZoneForm()
					this.zoneDone = false
					setTimeout(() => {
						this.router.visit(this.route('zone.page.group'))
					}, 3000)
				})
			})
			.catch((e) => {
				push.error({
					title: 'Error',
					message: 'Unable to create zone at this time. Please try again later.',
				})
			})
			.finally(() => (this.zoneLoading = false))
	}
}

export default toNative(ZoneManagement)
</script>
